import {
  ActivatedRouteSnapshot,
  DetachedRouteHandle,
  RouteReuseStrategy,
} from "@angular/router";

/**
 * Keeps the component instance (and all its data signals) alive when the
 * user navigates away from a route marked `data: { reuse: true }` and then
 * returns — so dashboards don't refetch on every back-click.
 *
 * Cleared on hard refresh (the class is re-instantiated with the app).
 * Explicit "Generate" actions still trigger a fetch inside the component.
 */
export class ReuseOnDataRouteStrategy implements RouteReuseStrategy {
  private handles = new Map<string, DetachedRouteHandle>();

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return route.data?.["reuse"] === true;
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
    const key = this.keyFor(route);
    if (!key) return;
    this.handles.set(key, handle);
    /* ng-select dropdowns use `appendTo="body"`, so the floating panel
       lives outside the component view. When this strategy detaches the
       route, the panel stays attached to <body> and floats over the next
       page. Synthesize an outside-click so ng-select closes any open
       panel cleanly via its own listener. */
    this.closeFloatingPanels();
  }

  private closeFloatingPanels(): void {
    if (typeof document === "undefined") return;
    const panels = document.querySelectorAll(".ng-dropdown-panel");
    if (panels.length === 0) return;
    /* ng-select listens for `click` (its default `outsideClickEvent`) on
       the document with capture:true; synthesize one targeted at <body>
       so each open panel closes via its own handler. Then drop any
       lingering DOM as a belt-and-suspenders against ng-select state
       desync from the route detach happening mid-frame. */
    document.body.dispatchEvent(
      new MouseEvent("click", { bubbles: true, cancelable: true }),
    );
    document.querySelectorAll(".ng-dropdown-panel").forEach((el) => el.remove());
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const key = this.keyFor(route);
    return !!key && this.handles.has(key);
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const key = this.keyFor(route);
    if (!key) return null;
    return this.handles.get(key) ?? null;
  }

  shouldReuseRoute(
    future: ActivatedRouteSnapshot,
    curr: ActivatedRouteSnapshot,
  ): boolean {
    return future.routeConfig === curr.routeConfig;
  }

  private keyFor(route: ActivatedRouteSnapshot): string | null {
    return route.routeConfig?.path ?? null;
  }
}
